import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, LogOut, ShieldCheck, ArrowLeft, MessageCircle, ReceiptText, FileText, Sparkles } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { Avatar } from "@/components/common";
import CockpitHelp from "./CockpitHelp";
import { useCockpit } from "./cockpitContext";

export function CockpitTopBar({ accent, base, clientId, company, navItems = [], overview = {}, user, t, status, onLogout, heading }) {
  return (
    <div className="hidden lg:flex sticky top-0 z-30 items-center gap-5 px-8 h-[72px] bg-[#0A0A0C]/85 backdrop-blur-xl border-b border-white/10" data-testid="cockpit-topbar">
      {heading && (
        <div className="min-w-0 shrink-0 max-w-[280px]" data-testid="topbar-heading">
          {heading.welcome && <p className="text-xs font-semibold flex items-center gap-1.5 truncate" style={{ color: accent }}><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} /> {heading.welcome}</p>}
          <h1 className="text-xl font-black tracking-tight leading-none mt-0.5 truncate">{heading.title}</h1>
        </div>
      )}
      <div className="flex-1 flex justify-center min-w-0">
        <CockpitSearch navItems={navItems} overview={overview} base={base} accent={accent} t={t} />
      </div>
      <div className="flex items-center gap-2.5 shrink-0">
        {clientId && (
          <div className="flex items-center gap-2" data-testid="topbar-impersonation">
            <span data-testid="topbar-impersonation-badge" title={company || ""}
              className="flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-full border"
              style={{ background: "rgba(245,158,11,0.12)", color: "#fbbf24", borderColor: "rgba(245,158,11,0.3)" }}>
              <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden 2xl:inline">{t("Super-Admin-Ansicht")}</span>
              <span className="2xl:hidden">Admin</span>
            </span>
            <button onClick={onLogout} data-testid="topbar-back-admin"
              className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-200 hover:bg-white/10 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5 shrink-0" /> <span className="hidden xl:inline">{t("Zurück zum Admin")}</span>
            </button>
            <span className="h-6 w-px bg-white/10" />
          </div>
        )}
        <NotificationBell clientId={clientId} accent={accent} base={base} />
        <CockpitHelp variant="desktop" />
        <ProfileMenu user={user} accent={accent} base={base} clientId={clientId} t={t} onLogout={onLogout} />
      </div>
    </div>
  );
}

function CockpitSearch({ navItems, overview, base, accent, t }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const items = useMemo(() => {
    const list = navItems.map((it) => ({ type: "nav", label: it.label, to: it.to, icon: it.icon }));
    (overview.recent_tickets || []).forEach((tk) => list.push({ type: "ticket", label: tk.subject, sub: tk.ticket_no, to: `${base}/support`, icon: MessageCircle }));
    (overview.recent_invoices || []).forEach((iv) => list.push({ type: "invoice", label: iv.number || iv.service_period, sub: t("Rechnung"), to: `${base}/invoices`, icon: ReceiptText }));
    (overview.recent_documents || []).forEach((d) => list.push({ type: "doc", label: d.title, sub: t("Dokument"), to: `${base}/documents`, icon: FileText }));
    return list;
  }, [navItems, overview, base, t]);

  const ql = q.trim().toLowerCase();
  const matches = ql ? items.filter((i) => (i.label || "").toLowerCase().includes(ql) || (i.sub || "").toLowerCase().includes(ql)).slice(0, 8) : [];
  const go = (to) => { setOpen(false); setQ(""); nav(to); };

  return (
    <div ref={ref} className="relative w-full max-w-md">
      <div className="flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/10 px-3 h-10 transition-colors focus-within:border-white/25">
        <Search className="w-4 h-4 text-gray-500 shrink-0" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} data-testid="cockpit-search-input"
          placeholder={t("Suchen…")} className="bg-transparent text-sm outline-none flex-1 placeholder:text-gray-500 text-white" />
      </div>
      {open && ql && (
        <div className="absolute mt-2 w-full rounded-xl bg-[#15151b] border border-white/10 shadow-2xl overflow-hidden z-50" data-testid="cockpit-search-results">
          {matches.length ? matches.map((m, i) => (
            <button key={i} onClick={() => go(m.to)} data-testid={`cockpit-search-result-${i}`} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/5 transition-colors">
              <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><m.icon className="w-4 h-4" /></span>
              <span className="flex-1 min-w-0"><span className="block text-sm truncate text-gray-100">{m.label}</span>{m.sub && <span className="block text-[11px] text-gray-500 truncate">{m.sub}</span>}</span>
            </button>
          )) : <div className="px-3 py-4 text-sm text-gray-500 text-center" data-testid="cockpit-search-empty">{t("Keine Treffer")}</div>}
        </div>
      )}
    </div>
  );
}

function ProfileMenu({ user, accent, base, clientId, t, onLogout }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  const { startTour } = useCockpit() || {};
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const name = user?.name || user?.email || "Benutzer";
  const roleLabel = clientId ? t("Super-Admin-Ansicht") : (user?.role === "client_staff" ? t("Mitarbeiter") : t("Kunden-Cockpit"));

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="cockpit-profile-btn"
        className="flex items-center gap-2 rounded-xl pl-1.5 pr-2 py-1.5 hover:bg-white/5 transition-colors">
        <Avatar name={name} color={accent} size={32} />
        <div className="hidden xl:block text-left leading-tight">
          <div className="text-sm font-semibold truncate max-w-[150px]">{name}</div>
          <div className="text-[10px] text-gray-500 truncate max-w-[150px]">{roleLabel}</div>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-xl bg-[#15151b] border border-white/10 shadow-2xl overflow-hidden z-50" data-testid="cockpit-profile-menu">
          <div className="px-3 py-2.5 border-b border-white/10">
            <div className="text-sm font-semibold truncate">{name}</div>
            <div className="text-[11px] text-gray-500 truncate">{user?.email}</div>
          </div>
          <button onClick={() => { setOpen(false); nav(`${base}/security`); }} data-testid="cockpit-profile-security" className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-white/5 transition-colors">
            <ShieldCheck className="w-4 h-4" style={{ color: accent }} /> {t("Konto & Sicherheit")}
          </button>
          <button onClick={() => { setOpen(false); startTour?.(); }} data-testid="cockpit-profile-explain" className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-white/5 transition-colors">
            <Sparkles className="w-4 h-4" style={{ color: accent }} /> {t("Cockpit erklären")}
          </button>
          <button onClick={() => { setOpen(false); onLogout(); }} data-testid="cockpit-profile-logout" className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-white/5 transition-colors text-gray-300">
            {clientId ? <><ArrowLeft className="w-4 h-4" /> {t("Zurück zum Admin")}</> : <><LogOut className="w-4 h-4" /> {t("Abmelden")}</>}
          </button>
        </div>
      )}
    </div>
  );
}
